import React, { useState } from "react";
import {
  Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MenuBoard from "../../../../Components/MenuBoard/MenuBoard";
import { NAVY, TEXT_SECONDARY, WARNING, SUCCESS } from "../../../../theme/tokens";
import { useBuildPosDraftQuery } from "../../../../Api/attoDeskApi";

const POS_TEAL = "#00838f";

/**
 * Preview uses the SAME normalized payload + MenuBoard renderer as device
 * playback, positioned on a template-shaped canvas. It is read-only — building
 * a draft never publishes or touches the live template.
 */
const MenuBoardPreviewDialog: React.FC<{
  open: boolean; onClose: () => void; connectionId: number; templateId: number;
}> = ({ open, onClose, connectionId, templateId }) => {
  const [orient, setOrient] = useState<"landscape" | "portrait">("landscape");
  const { data, isFetching } = useBuildPosDraftQuery(
    { id: connectionId, template_id: templateId },
    { skip: !open },
  );
  const draft = data?.data as any;
  const zones: any[] = draft?.zones ?? [];
  const warnings: any[] = draft?.warnings ?? [];
  const stats = draft?.stats ?? {};

  const aspect = orient === "landscape" ? "16 / 9" : "9 / 16";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 800, color: NAVY }}>Menu preview</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={orient} onChange={(_, v) => v && setOrient(v)}>
            <ToggleButton value="landscape" sx={{ textTransform: "none" }}>16:9</ToggleButton>
            <ToggleButton value="portrait" sx={{ textTransform: "none" }}>9:16</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isFetching ? (
          <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: POS_TEAL }} /></Box>
        ) : (
          <Stack spacing={2}>
            <Box sx={{
              position: "relative", width: "100%", aspectRatio: aspect,
              maxHeight: "60vh", mx: "auto", background: "#0B1E2D",
              borderRadius: 2, overflow: "hidden",
              ...(orient === "portrait" ? { maxWidth: 360 } : {}),
            }}>
              {zones.length === 0 && (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
                    No menu-board zones mapped yet.
                  </Typography>
                </Box>
              )}
              {zones.map((z, i) => {
                const zm = z.zone;
                // Position mapped zones by their template percentages; a mapping
                // with a missing zone (zm null) is surfaced in warnings instead.
                if (!zm) return null;
                return (
                  <Box key={z.zone_key ?? i} sx={{
                    position: "absolute",
                    left: `${zm.x}%`, top: `${zm.y}%`, width: `${zm.w}%`, height: `${zm.h}%`,
                    overflow: "hidden",
                  }}>
                    <MenuBoard payload={z.payload} />
                  </Box>
                );
              })}
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${stats.mapped_zones ?? 0} zones`} sx={{ fontWeight: 700 }} />
              <Chip size="small" label={`${stats.unmapped_categories ?? 0} unmapped categories`} sx={{ fontWeight: 700 }} />
              <Chip size="small" label={`${stats.hidden_items ?? 0} hidden`} sx={{ fontWeight: 700 }} />
              <Chip size="small" label={`${stats.unavailable_items ?? 0} unavailable`} sx={{ fontWeight: 700 }} />
              <Chip size="small" icon={warnings.length ? <WarningAmberIcon /> : undefined}
                label={warnings.length ? `${warnings.length} warnings` : "No warnings"}
                sx={{ fontWeight: 700, color: warnings.length ? WARNING : SUCCESS,
                  bgcolor: warnings.length ? "rgba(234,88,12,0.12)" : "rgba(22,163,74,0.12)" }} />
            </Stack>

            {warnings.length > 0 && (
              <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                {warnings.map((w, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.4 }}>
                    <WarningAmberIcon sx={{ fontSize: 16, color: WARNING }} />
                    <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY }}>{w.message}</Typography>
                  </Box>
                ))}
              </Box>
            )}
            <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>
              Preview only — building a draft never changes your live screens. Publishing
              lands in the next phase.
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MenuBoardPreviewDialog;
